"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const navBg =
    scrolled || !isHome || mobileOpen
      ? "bg-navy/95 backdrop-blur-md shadow-lg"
      : "bg-transparent";

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        navBg
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link
            href="/"
            aria-label="LexGo Invest — home"
            className="flex items-center gap-2.5 font-heading text-xl font-bold tracking-tight"
          >
            {/* Brand mark: decorative — the adjacent wordmark text carries
                the semantics, so alt="" + aria-label on the parent Link. */}
            <Image
              src="/brand/lexgoinvest-logo.svg"
              alt=""
              width={32}
              height={32}
              priority
              className="shrink-0"
            />
            <span>
              <span className="text-teal">LexGo</span>{" "}
              <span className="text-gold">Invest</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-teal-lt",
                  pathname === link.href ? "text-teal-lt" : "text-white/80"
                )}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/apply"
              className="rounded-lg bg-gold px-5 py-2 text-sm font-semibold text-navy transition-all hover:bg-gold-lt hover:scale-105 hover:shadow-lg"
            >
              Apply &rarr;
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-navy/95 backdrop-blur-md border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="block text-white/80 hover:text-teal-lt py-2 text-sm font-medium"
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/apply"
              className="block w-full rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-navy mt-2 text-center"
            >
              Apply &rarr;
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
